/**
 * Performance optimization utilities
 * Implements lazy loading, memoization, and other performance enhancements
 */

import { memo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Debounce utility for search and input handling
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Throttle utility for scroll and resize events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Virtual scrolling utility for large lists
 */
export class VirtualScroller {
  constructor({
    itemHeight = 100,
    containerHeight = 400,
    overscan = 5
  } = {}) {
    this.itemHeight = itemHeight;
    this.containerHeight = containerHeight;
    this.overscan = overscan;
  }

  /**
   * Calculate visible items based on scroll position
   * @param {number} scrollTop - Current scroll position
   * @param {number} totalItems - Total number of items
   * @returns {Object} - Visible range and offset
   */
  getVisibleRange(scrollTop, totalItems) {
    const visibleItemCount = Math.ceil(this.containerHeight / this.itemHeight);
    const startIndex = Math.floor(scrollTop / this.itemHeight);
    const endIndex = Math.min(
      startIndex + visibleItemCount + this.overscan,
      totalItems - 1
    );

    const visibleStartIndex = Math.max(0, startIndex - this.overscan);
    const offsetY = visibleStartIndex * this.itemHeight;

    return {
      startIndex: visibleStartIndex,
      endIndex,
      offsetY,
      totalHeight: totalItems * this.itemHeight
    };
  }
}

/**
 * Enhanced image optimization utilities
 */

/**
 * Optimized image formats based on browser support
 */
const getOptimizedImageFormat = (originalSrc) => {
  // Check for WebP support
  const supportsWebP = (() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  })();

  // Check for AVIF support
  const supportsAVIF = (() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
  })();

  // Return optimized format if available
  if (supportsAVIF && originalSrc.includes('placeholder')) {
    return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.avif');
  }
  if (supportsWebP && originalSrc.includes('placeholder')) {
    return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  }

  return originalSrc;
};

/**
 * Generate responsive image sources.
 *
 * This previously appended a `?w=NNN` query string per size to build a
 * srcSet, but our images are plain static files (public/Sponsors,
 * public/*.png) with no resizing backend behind them — every `?w=` variant
 * serves the exact same full-size file, so the "responsive" srcSet only
 * caused the browser to request the same image two or three extra times
 * for no benefit. Worse, several of those filenames contain spaces (e.g.
 * "Liberator Ltd.jpg") which aren't valid unencoded inside a srcSet's
 * space-delimited "url widthw" syntax, so the browser was parsing part of
 * the filename as an invalid width descriptor and silently dropping the
 * candidate — the "Failed parsing 'srcset'" warnings on the Sponsors page.
 * Until there's an actual image CDN/resizing service to point multiple
 * real sizes at, don't generate a srcSet at all.
 */
const generateResponsiveSources = (src) => ({ src, srcSet: '' });

/**
 * Enhanced lazy loading image component with optimization
 */
export const LazyImage = memo(({
  src,
  alt,
  className,
  placeholder,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  loading = 'lazy',
  priority = false,
  onLoad,
  onError,
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Load immediately if priority
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();
  const [optimizedSrc, setOptimizedSrc] = useState(src);

  // Optimize image format on mount
  useEffect(() => {
    setOptimizedSrc(getOptimizedImageFormat(src));
  }, [src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || isInView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isInView]);

  const handleLoad = useCallback((event) => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.(event);
  }, [onLoad]);

  const handleError = useCallback((event) => {
    setHasError(true);
    setIsLoaded(false);
    onError?.(event);
  }, [onError]);

  const { srcSet } = generateResponsiveSources(optimizedSrc);

  const imageStyle = {
    opacity: isLoaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
    width: '100%',
    height: 'auto',
    ...style
  };

  const placeholderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    color: '#666',
    minHeight: '200px',
    fontSize: '14px'
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${className || ''}`}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {(isInView || priority) && (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={priority ? 'eager' : loading}
          onLoad={handleLoad}
          onError={handleError}
          style={imageStyle}
          {...props}
        />
      )}

      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div
          className="image-placeholder"
          style={placeholderStyle}
        >
          {placeholder || (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }} />
              Loading image...
            </div>
          )}
        </div>
      )}

      {/* Error placeholder */}
      {hasError && (
        <div
          className="image-error"
          style={{
            ...placeholderStyle,
            backgroundColor: '#ffe6e6',
            color: '#d32f2f'
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}>⚠️</span>
            Failed to load image
          </div>
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

LazyImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.node,
  sizes: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  priority: PropTypes.bool,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  style: PropTypes.object
};

/**
 * Image preloader utility
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Batch image preloader
 */
export const preloadImages = async (srcArray) => {
  try {
    const promises = srcArray.map(src => preloadImage(src));
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.warn('Some images failed to preload:', error);
    return false;
  }
};

/**
 * Cache utility for API responses
 */
class CacheManager {
  constructor(maxSize = 100, ttl = 300000) { // 5 minutes default TTL
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  /**
   * Get cached value
   * @param {string} key - Cache key
   * @returns {any} - Cached value or null
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;

    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Set cached value
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   */
  set(key, value) {
    // Remove oldest item if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      value,
      expiry: Date.now() + this.ttl
    });
  }

  /**
   * Clear cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: this.hitCount / (this.hitCount + this.missCount) || 0
    };
  }
}

/**
 * Global cache instances
 */
export const cacheManager = {
  sessions: new CacheManager(50, 600000), // 10 minutes for sessions
  questions: new CacheManager(100, 300000), // 5 minutes for questions
  announcements: new CacheManager(20, 180000), // 3 minutes for announcements
  general: new CacheManager(100, 300000) // 5 minutes for general data
};

/**
 * Performance monitoring utilities
 */
export const performanceMonitor = {
  /**
   * Measure component render time
   * @param {string} componentName - Name of the component
   * @param {Function} renderFunction - Function to measure
   */
  measureRender: (componentName, renderFunction) => {
    const startTime = performance.now();
    const result = renderFunction();
    const endTime = performance.now();

    if (endTime - startTime > 16) { // Longer than one frame (60fps)
      console.warn(`Slow render detected in ${componentName}: ${endTime - startTime}ms`);
    }

    return result;
  },

  /**
   * Measure API call performance
   * @param {string} apiName - Name of the API call
   * @param {Function} apiCall - API function to measure
   */
  measureApiCall: async (apiName, apiCall) => {
    const startTime = performance.now();
    try {
      const result = await apiCall();
      const endTime = performance.now();

      if (endTime - startTime > 1000) { // Longer than 1 second
        console.warn(`Slow API call detected for ${apiName}: ${endTime - startTime}ms`);
      }

      return result;
    } catch (error) {
      const endTime = performance.now();
      console.error(`API call failed for ${apiName} after ${endTime - startTime}ms:`, error);
      throw error;
    }
  },

  /**
   * Monitor memory usage
   */
  checkMemoryUsage: () => {
    if ('memory' in performance) {
      const memory = performance.memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
      const totalMB = Math.round(memory.totalJSHeapSize / 1048576);
      const limitMB = Math.round(memory.jsHeapSizeLimit / 1048576);

      if (usedMB > limitMB * 0.8) {
        console.warn(`High memory usage detected: ${usedMB}MB / ${limitMB}MB`);
      }

      return { used: usedMB, total: totalMB, limit: limitMB };
    }
    return null;
  }
};

/**
 * Bundle size optimization utilities
 */
export const bundleOptimization = {
  /**
   * Preload critical resources
   */
  preloadCriticalResources: () => {
    // Preload critical CSS
    const criticalCSS = document.createElement('link');
    criticalCSS.rel = 'preload';
    criticalCSS.as = 'style';
    criticalCSS.href = '/css/critical.css';
    document.head.appendChild(criticalCSS);

    // Preload critical fonts
    const criticalFont = document.createElement('link');
    criticalFont.rel = 'preload';
    criticalFont.as = 'font';
    criticalFont.type = 'font/woff2';
    criticalFont.href = '/fonts/inter-var.woff2';
    criticalFont.crossOrigin = 'anonymous';
    document.head.appendChild(criticalFont);
  }
};

const performanceOptimizations = {
  debounce,
  throttle,
  VirtualScroller,
  LazyImage,
  cacheManager,
  performanceMonitor,
  bundleOptimization
};

export default performanceOptimizations;
